import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const Automotive = () => {
  const solutions = [
    {
      icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      title: "Smart Manufacturing",
      description: "AI-powered production optimization, predictive maintenance, and quality control systems that reduce downtime and improve efficiency."
    },
    {
      icon: "M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z",
      title: "Autonomous Vehicle Systems",
      description: "Advanced AI algorithms for self-driving technology, sensor fusion, and real-time decision-making systems."
    },
    {
      icon: "M13 10V3L4 14h7v7l9-11h-7z",
      title: "Electric Vehicle Infrastructure",
      description: "Smart charging solutions, battery management systems, and IoT-enabled infrastructure for EV ecosystems."
    },
    {
      icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
      title: "Supply Chain Optimization",
      description: "Real-time tracking, demand forecasting, and automated inventory management for automotive supply chains."
    },
    {
      icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
      title: "Connected Car Platforms",
      description: "IoT solutions for vehicle connectivity, telematics, and advanced driver assistance systems (ADAS)."
    },
    {
      icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
      title: "Customer Experience",
      description: "AI-powered personalization, virtual showrooms, and intelligent customer service solutions."
    }
  ];

  const useCases = [
    {
      title: "Predictive Maintenance",
      challenge: "Unplanned downtime costs manufacturers millions annually",
      solution: "ML-powered predictive analytics reduce breakdowns by 40%",
      result: "35% reduction in maintenance costs, 50% decrease in unplanned downtime"
    },
    {
      title: "Quality Control Automation",
      challenge: "Manual inspection processes are slow and inconsistent",
      solution: "Computer vision systems detect defects with 99.5% accuracy",
      result: "60% faster inspection, 25% reduction in defect rates"
    },
    {
      title: "Supply Chain Intelligence",
      challenge: "Complex global supply chains lack real-time visibility",
      solution: "AI-driven analytics provide end-to-end supply chain insights",
      result: "30% improvement in on-time delivery, 20% inventory reduction"
    }
  ];

  const technologies = [
    { name: "Computer Vision", usage: "Quality inspection, autonomous driving" },
    { name: "Predictive Analytics", usage: "Maintenance, demand forecasting" },
    { name: "IoT Integration", usage: "Connected vehicles, smart factories" },
    { name: "Machine Learning", usage: "Process optimization, defect detection" },
    { name: "Natural Language Processing", usage: "Voice assistants, customer service" },
    { name: "Digital Twin", usage: "Virtual prototyping, simulation" }
  ];

  const stats = [
    { value: "40%", label: "Reduction in Maintenance Costs" },
    { value: "99.5%", label: "Defect Detection Accuracy" },
    { value: "35%", label: "Manufacturing Efficiency Gain" },
    { value: "50%", label: "Faster Time-to-Market" }
  ];

  return (
    <>
      <SEO 
        title="Automotive Solutions - AI & Digital Transformation for Auto Industry"
        description="Transform your automotive business with AI-powered manufacturing, autonomous vehicle systems, smart supply chain, and connected car platforms. Drive innovation in the automotive industry."
        keywords="automotive AI, smart manufacturing, autonomous vehicles, EV infrastructure, connected cars, supply chain optimization, automotive digital transformation"
      />

      {/* Hero Section */}
      <section className="relative pt-20 md:pt-24 pb-16 md:pb-20 bg-gradient-to-br from-alabaster via-pearl-white to-blush-light/20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #E63946 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-platinum mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-primary-red rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-medium text-graphite">Industry Solutions</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              <span className="text-gradient">Automotive</span> Industry Solutions
            </h1>
            
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Drive innovation with AI-powered manufacturing, autonomous systems, and intelligent supply chain solutions designed for the future of automotive.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Schedule Consultation
              </Link>
              <Link to="/services" className="btn-secondary">
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">{stat.value}</div>
                <div className="text-sm text-titanium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Challenges */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Automotive Industry Challenges
            </h2>
            <p className="text-lg text-titanium">
              The automotive industry faces unprecedented transformation driven by electrification, automation, and changing consumer expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Manufacturing Complexity",
                description: "Modern vehicles require coordination of thousands of components from global suppliers",
                impact: "High"
              },
              {
                title: "Quality Standards",
                description: "Zero-defect manufacturing is critical for safety and brand reputation",
                impact: "Critical"
              },
              {
                title: "Supply Chain Disruptions",
                description: "Global dependencies create vulnerability to disruptions and delays",
                impact: "High"
              },
              {
                title: "EV Transition",
                description: "Rapid shift to electric vehicles requires new infrastructure and expertise",
                impact: "Critical"
              },
              {
                title: "Autonomous Technology",
                description: "Self-driving systems demand massive computing power and real-time processing",
                impact: "High"
              },
              {
                title: "Customer Expectations",
                description: "Consumers demand personalization, connectivity, and seamless experiences",
                impact: "Medium"
              }
            ].map((challenge, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300 group">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-charcoal group-hover:text-primary-red transition-colors">
                    {challenge.title}
                  </h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    challenge.impact === 'Critical' 
                      ? 'bg-red-100 text-red-700' 
                      : challenge.impact === 'High'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {challenge.impact}
                  </span>
                </div>
                <p className="text-sm text-titanium">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Our Automotive Solutions
            </h2>
            <p className="text-lg text-titanium">
              Comprehensive AI and technology solutions designed specifically for automotive manufacturers, suppliers, and dealers.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div key={index} className="group">
                <div className="card hover:shadow-xl transition-all duration-300 h-full bg-gradient-to-br from-white to-alabaster">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-red to-deep-garnet rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={solution.icon} />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                    {solution.title}
                  </h3>
                  <p className="text-titanium">{solution.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Real-World Success Stories
            </h2>
            <p className="text-lg text-titanium">
              See how leading automotive companies are transforming their operations with our solutions.
            </p>
          </div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="card bg-white hover:shadow-xl transition-all duration-300">
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-primary-red mb-2">CHALLENGE</h4>
                    <p className="text-sm text-titanium">{useCase.challenge}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-red mb-2">SOLUTION</h4>
                    <p className="text-sm text-titanium">{useCase.solution}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary-red mb-2">RESULTS</h4>
                    <p className="text-sm font-bold text-charcoal">{useCase.result}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-platinum">
                  <h3 className="text-lg font-semibold text-charcoal">{useCase.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Advanced Technologies
            </h2>
            <p className="text-lg text-titanium">
              Cutting-edge AI and technology stack powering automotive innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="card hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-pearl-white">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-red rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h3 className="font-semibold text-charcoal mb-1">{tech.name}</h3>
                    <p className="text-sm text-titanium">{tech.usage}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Why Choose Abacies for Automotive
              </h2>
              <p className="text-lg text-titanium">
                Deep industry expertise combined with cutting-edge technology.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="card bg-white hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">Industry Expertise</h3>
                    <p className="text-titanium">12+ years working with automotive OEMs, Tier 1 suppliers, and dealership networks</p>
                  </div>
                </div>
              </div>

              <div className="card bg-white hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">Rapid Deployment</h3>
                    <p className="text-titanium">Proven methodologies for quick implementation with minimal disruption to operations</p>
                  </div>
                </div>
              </div>

              <div className="card bg-white hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">Measurable Results</h3>
                    <p className="text-titanium">Data-driven approach with clear KPIs and ROI tracking from day one</p>
                  </div>
                </div>
              </div>

              <div className="card bg-white hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">Enterprise Security</h3>
                    <p className="text-titanium">ISO 27001 certified with automotive-grade security protocols and compliance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Partners */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Seamless Integration
            </h2>
            <p className="text-lg text-titanium">
              We integrate with leading automotive systems and platforms.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {['SAP', 'Oracle', 'Siemens', 'PTC', 'CATIA', 'AutoCAD', 'Teamcenter', 'ADAS'].map((system, index) => (
              <div key={index} className="card text-center hover:shadow-lg transition-all duration-300">
                <div className="text-lg font-semibold text-charcoal">{system}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary-red to-deep-garnet text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Automotive Operations?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss how our AI solutions can drive innovation and efficiency in your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-16 bg-alabaster">
        <div className="page-container">
          <h2 className="text-3xl font-bold mb-8 text-charcoal text-center">Related Industry Solutions</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/solutions/manufacturing-logistics" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold text-charcoal group-hover:text-primary-red transition-colors mb-2">
                Manufacturing & Logistics
              </h3>
              <p className="text-sm text-titanium mb-3">
                Smart factory solutions and supply chain optimization
              </p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            
            <Link to="/solutions/retail-ecommerce" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold text-charcoal group-hover:text-primary-red transition-colors mb-2">
                Retail & E-commerce
              </h3>
              <p className="text-sm text-titanium mb-3">
                Customer experience and omnichannel solutions
              </p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
            
            <Link to="/solutions/energy-utilities" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold text-charcoal group-hover:text-primary-red transition-colors mb-2">
                Energy & Utilities
              </h3>
              <p className="text-sm text-titanium mb-3">
                Smart grid and renewable energy solutions
              </p>
              <span className="text-primary-red text-sm font-semibold">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Automotive;